/**
 * Estimated marketplace commission the merchant avoided by selling direct
 * through their own storefront (vs Shopee / Lazada). Backed by
 * zaviago_backend.api.marketplace_savings.get_marketplace_fee_savings.
 */
import { useQuery } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

export interface SavingsBucket {
  direct_gmv: number;
  saved_vs_shopee: number;
  saved_vs_lazada: number;
  direct_orders: number;
  by_category: {
    category: string;
    gmv: number;
    saved_vs_shopee: number;
    saved_vs_lazada: number;
  }[];
}

export interface MonthlyPoint {
  month: string; // "YYYY-MM"
  saved: number;
}

export interface ChannelMix {
  direct: number;
  shopee: number;
  lazada: number;
  tiktok: number;
  other: number;
  total: number;
}

export interface MarketplaceFeesPaid {
  shopee: number;
  lazada: number;
  tiktok: number;
  total: number;
  gmv: { shopee: number; lazada: number; tiktok: number };
}

export interface MarketplaceSavings {
  currency: string;
  all_time: SavingsBucket;
  this_month: SavingsBucket;
  monthly_trend: MonthlyPoint[];
  channel_mix: ChannelMix;
  marketplace_fees_paid: MarketplaceFeesPaid;
  untapped_saved: number;
  month_days_elapsed: number;
  month_days_total: number;
  estimate_note: string;
}

export function useMarketplaceSavings() {
  return useQuery({
    queryKey: ["marketplace-fee-savings"],
    queryFn: () =>
      frappeMethod<MarketplaceSavings>(
        "zaviago_backend.api.marketplace_savings.get_marketplace_fee_savings"
      ),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
